import React, { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import {
  useGetHomeQuery,
  useGetReferralProgramStatusQuery,
  useSubmitRegistrationRequestMutation,
} from "@/store/rest_api";
import { useAuth } from "@/hooks/useAuth";
import { getEmailValidationPattern } from "@/util/validation";
import { apiErrorMessage } from "@/util/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RegistrationRequestData } from "@/model/interface/RegistrationRequest";

export default function RequestAccess() {
  const { isAuthenticated } = useAuth();
  const { data: userData } = useGetHomeQuery(undefined, { skip: !isAuthenticated });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: referralProgram } = useGetReferralProgramStatusQuery();
  const [submitRequest, submitResult] = useSubmitRegistrationRequestMutation();
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FieldValues>({ defaultValues: { referralCode: searchParams.get("ref") ?? "" } });

  const onSubmit = async (data: FieldValues) => {
    try {
      await submitRequest(data as RegistrationRequestData).unwrap();
      setSubmitted(true);
    } catch {
      setSubmitted(false);
    }
  };

  useEffect(() => {
    if (userData) {
      navigate("/dashboard");
    }
  }, [userData, navigate]);

  if (submitted) {
    return (
      <div className="min-h-svh w-full flex items-center justify-center bg-muted text-foreground p-6">
        <div className="w-full max-w-md space-y-6">
          <div className="flex justify-center">
            <Link to="/">
              <img src="/assets/images/retreevo-logo.svg" alt="" className="h-10 w-auto" />
            </Link>
          </div>
          <div className="rounded-xl border bg-card shadow-sm p-8 space-y-4 text-center">
            <div className="flex justify-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <MailCheck className="h-6 w-6 text-primary" />
              </div>
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">Request received</h1>
            <p className="text-sm text-muted-foreground">
              Thanks for your interest in Retreevo. Most requests are reviewed in under 2 hours.
              You'll get an email with your invite link as soon as you're approved.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-svh w-full flex items-center justify-center bg-muted text-foreground p-6">
      <div className="w-full max-w-md space-y-6">
        <div className="flex justify-center">
          <Link to="/">
            <img src="/assets/images/retreevo-logo.svg" alt="" className="h-10 w-auto" />
          </Link>
        </div>

        <div className="rounded-xl border bg-card shadow-sm p-8 space-y-6">
          <div className="space-y-1 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">Request access</h1>
            <p className="text-sm text-muted-foreground">
              Retreevo is currently invite-only. Tell us a bit about your business and we'll send
              you an invite.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  type="text"
                  autoComplete="given-name"
                  required
                  {...register("firstName", { required: true, minLength: 2, maxLength: 50 })}
                />
                {errors.firstName && (
                  <p className="text-xs text-destructive">First name is required</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  type="text"
                  autoComplete="family-name"
                  required
                  {...register("lastName", { required: true, minLength: 2, maxLength: 50 })}
                />
                {errors.lastName && (
                  <p className="text-xs text-destructive">Last name is required</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                autoComplete="email"
                required
                {...register("email", { required: true, pattern: getEmailValidationPattern() })}
              />
              {errors.email && (
                <p className="text-xs text-destructive">A valid email is required</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="company">Company</Label>
              <Input
                id="company"
                type="text"
                placeholder="Acme Real Estate"
                autoComplete="organization"
                {...register("company", { maxLength: 100 })}
              />
            </div>

            {referralProgram?.enabled && (
              <div className="space-y-1.5">
                <Label htmlFor="referralCode">Referral code</Label>
                <Input
                  id="referralCode"
                  type="text"
                  placeholder="Optional"
                  autoComplete="off"
                  {...register("referralCode", { maxLength: 16 })}
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="message">What will you use Retreevo for?</Label>
              <Textarea
                id="message"
                rows={3}
                placeholder="A sentence or two about your business helps us approve you faster."
                {...register("message", { maxLength: 1000 })}
              />
            </div>

            {submitResult.isError && (
              <p className="text-sm text-destructive">
                {apiErrorMessage(submitResult.error, "Something went wrong. Please try again.")}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={submitResult.isLoading}>
              {submitResult.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Request an invite
            </Button>
          </form>

          <div className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-foreground hover:text-primary">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
